import React, { useEffect, useState } from "react";
import axios from "axios";
import { base_url } from "../../utils/axiosConfig";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { IoSearch } from "react-icons/io5";
import toast from "react-hot-toast";
import { BsEye } from "react-icons/bs";
import Select from 'react-select';

const AssignOrders = () => {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [showItems, setShowItems] = useState(false);

  // modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [status, setStatus] = useState("");
  const [file, setFile] = useState(null);

  // return reason states
  const [returnReason, setReturnReason] = useState("");
  const [customReason, setCustomReason] = useState("");

  // preview modal
  const [showPreview, setShowPreview] = useState(false);
  const [previewOrder, setPreviewOrder] = useState(null);

  const options = [
    { value: 'Damaged Product', label: 'Damaged Product' },
    { value: 'Wrong Item', label: 'Wrong Item or Wrong Address' },
    { value: 'Late Delivery', label: 'Refused Order' },
    { value: 'Others', label: 'Others' },
  ];

  const statusOptions = [
    { value: 'Out for Delivery', label: 'Out for Delivery' },
    { value: 'Delivered', label: 'Delivered' },
    { value: 'Return', label: 'Return' },
  ];

  const updatableStatuses = ["Out for Delivery", "Ready to Ship"];

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const customer = localStorage.getItem("customer");
      if (!customer) {
        toast.error("Customer not found in local storage");
        return;
      }

      const parsedCustomer = JSON.parse(customer);
      const userId = parsedCustomer._id;
      if (!userId) {
        toast.error("Invalid customer ID");
        return;
      }

      const res = await axios.get(
        `${base_url}orders/delvery-boy/orders/${userId}`
      );

      setOrders(res.data.orders || []);
    } catch (error) {
      console.error("Error fetching assigned orders", error);
      toast.error("Failed to load assigned orders ❌");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, status, file) => {
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("orderId", orderId);
      formData.append("status", status);

      if (status === "Return") {
        const reasonToSend = returnReason === "Others" ? customReason : returnReason;
        formData.append("returnReason", reasonToSend || "Not specified");
      }

      if (file) {
        formData.append("orderImage", file);
      }

      await axios.put(`${base_url}orders/orders/update-status`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      await fetchOrders();
      toast.success(`Order status updated to "${status}" 🎉`);
    } catch (error) {
      console.error("Error updating order status", error);
      toast.error(error.response?.data?.message || "Failed to update order ❌");
    } finally {
      setUploading(false);
    }
  };

  const handleOpenModal = (order) => {
    setSelectedOrder(order);
    setStatus("");
    setFile(null);
    setReturnReason("");
    setCustomReason("");
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!selectedOrder || !status) {
      toast.error("Please select a status before saving");
      return;
    }
    await handleStatusUpdate(selectedOrder._id, status, file);
    setShowModal(false);
  };

  const handlePreview = (order) => {
    setPreviewOrder(order);
    setShowPreview(true);
  };

  const filteredOrders = orders.filter((order) => {
    const searchText = search.toLowerCase();

    const matchesSearch =
      order.trackingNumber?.toLowerCase().includes(searchText) ||
      order.shippingInfo?.firstName?.toLowerCase().includes(searchText) ||
      order.shippingInfo?.lastName?.toLowerCase().includes(searchText) ||
      order.shippingInfo?.city?.toLowerCase().includes(searchText) ||
      order.shippingInfo?.pincode?.toString().includes(searchText) ||
      order.orderStatus?.toLowerCase().includes(searchText);

    const matchesStatus =
      !statusFilter || order.orderStatus === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredOrders.length / pageSize);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="container-fluid py-3">
      <h4 className="mb-0 fw-bold">Assigned Orders</h4>

      {/* Search + Status Filter */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center my-3 ">
        <div
          className="d-flex align-items-center border rounded flex-grow-1 flex-md-grow-0 my-2"
          style={{ maxWidth: "350px" }}
        >
          <div className="px-2 border-end d-flex align-items-center text-muted">
            <IoSearch size={18} />
          </div>
          <input
            type="text"
            placeholder="Search orders..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="form-control border-0 shadow-none"
          />
        </div>

        <div className="d-flex flex-row gap-2 w-100 w-md-auto justify-content-md-end">
          {["", "Out for Delivery", "Delivered", "Return"].map((status) => (
            <button
              key={status}
              className={`btn btn-sm ${statusFilter === status ? "btn-primary" : "btn-outline-primary"
                }`}
              onClick={() => {
                setStatusFilter(status);
                setCurrentPage(1);
              }}
            >
              {status === "" ? "All Status" : status}
            </button>
          ))}
        </div>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ minHeight: "60vh" }}
        >
          <Spinner animation="border" role="status" variant="primary" />
          <span className="mt-2 text-muted small">Loading Orders...</span>
        </div>
      ) : paginatedOrders.length === 0 ? (
        <div className="alert alert-info small">No assigned orders found.</div>
      ) : (
        <div className="table-responsive shadow-sm rounded">
          <table className="table table-sm table-bordered table-hover align-middle text-nowrap">
            <thead className="table-dark text-center small">
              <tr>
                <th style={{ width: "50px" }}>S.No</th>
                <th>Customer Name</th>
                <th>Address</th>
                <th>Payment</th>
                <th>Total</th>
                <th>Phone No</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody className="small">
              {paginatedOrders.map((order, index) => (
                <tr key={order._id}>
                  <td className="text-center">
                    {(currentPage - 1) * pageSize + index + 1}.
                  </td>
                  <td>
                    {order.shippingInfo?.firstName
                      ? `${order.shippingInfo.firstName} ${order.shippingInfo.lastName || ""
                      }`
                      : `${order.user?.firstname || ""} ${order.user?.lastname || ""
                      }`}
                  </td>
                  <td className="text-truncate" style={{ maxWidth: "150px" }}>
                    {order.shippingInfo?.address}, {order.shippingInfo?.city}
                  </td>
                  <td>{order.paymentInfo?.cashOndelivery ? "COD" : "Prepaid"}</td>
                  <td>₹{order.totalPrice}</td>
                  <td>{order.user?.phoneNumber || "N/A"}</td>
                  <td>
                    <span
                      className={`badge rounded-pill ${order.orderStatus === "Delivered"
                          ? "bg-success"
                          : order.orderStatus === "Return"
                            ? "bg-danger"
                            : "bg-warning text-dark"
                        }`}
                    >
                      {order.orderStatus}
                    </span>
                  </td>
                  <td className="text-center d-flex gap-2 justify-content-center">
                    {/* Preview Button */}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handlePreview(order)}
                    >
                      <BsEye size={16} />
                    </Button>

                    {/* Update Button → hide if status is not in updatableStatuses */}
                    {updatableStatuses.includes(order.orderStatus) && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleOpenModal(order)}
                      >
                        Update
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
            className="btn btn-sm btn-outline-secondary"
          >
            Prev
          </button>
          <span className="small">
            Page <b>{currentPage}</b> of <b>{totalPages}</b>
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
            className="btn btn-sm btn-outline-secondary"
          >
            Next
          </button>
        </div>
      )}

      {uploading && (
        <div className="text-center mt-2 small text-muted">Updating order...</div>
      )}

      {/* Update Status Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update Order Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Select
                options={statusOptions}
                value={statusOptions.find((option) => option.value === status) || null}
                onChange={(selectedOption) => setStatus(selectedOption.value)}
                placeholder="Select status"
                isClearable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderRadius: '8px',
                    minHeight: '40px',
                    borderColor: '#ced4da',
                    fontSize: '16px',
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    color: '#6c757d',
                  }),
                }}
              />
            </Form.Group>

            {status === "Delivered" && (
              <Form.Group className="mt-3">
                <Form.Label>Upload Order Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </Form.Group>
            )}

            {status === "Return" && (
              <Form.Group className="mt-3">
                <Form.Label>Reason for Return</Form.Label>
                <Select
                  options={options}
                  value={options.find((option) => option.value === returnReason)}
                  onChange={(selectedOption) => setReturnReason(selectedOption.value)}
                  placeholder="Select a reason"
                  isClearable
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderRadius: '8px',
                      minHeight: '40px',
                    }),
                  }}
                />

                {returnReason === "Others" && (
                  <Form.Control
                    type="text"
                    placeholder="Enter your reason"
                    className="mt-2"
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                  />
                )}
              </Form.Group>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={
              !status ||
              (status === "Delivered" && !file) ||
              (status === "Return" && !returnReason) ||
              (status === "Return" && returnReason === "Others" && !customReason) ||
              uploading
            }
            onClick={handleSave}
          >
            {uploading ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Preview Modal */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Order Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewOrder ? (
            <div className="small">
              {/* Order & Status */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Tracking No: {previewOrder.trackingNumber}</h6>
                <span
                  className={`badge rounded-pill px-3 py-2 ${previewOrder.orderStatus?.toLowerCase() === "delivered"
                      ? "bg-success"
                      : previewOrder.orderStatus?.toLowerCase() === "Return"
                        ? "bg-danger"
                        : "bg-warning text-dark"
                    }`}
                >
                  {previewOrder.orderStatus}
                </span>
              </div>

              {/* Customer & Address */}
              <div className="border rounded p-3 mb-3 bg-light">
                <h6 className="mb-2">Customer</h6>
                <p className="mb-1">
                  {previewOrder.shippingInfo?.firstName} {previewOrder.shippingInfo?.lastName}
                </p>
                <p className="mb-1">📞 {previewOrder.user?.phoneNumber || "N/A"}</p>
                <p className="mb-1">✉️ {previewOrder.user?.email || "N/A"}</p>
              </div>

              <div className="border rounded p-3 mb-3 bg-light">
                <h6 className="mb-2">Shipping Address</h6>
                <p className="mb-1">
                  {previewOrder.shippingInfo?.address}, {previewOrder.shippingInfo?.landmark}
                </p>
                <p className="mb-1">
                  {previewOrder.shippingInfo?.city}, {previewOrder.shippingInfo?.state} -{" "}
                  {previewOrder.shippingInfo?.pincode}
                </p>
              </div>

              {/* Payment & Total */}
              <div className="d-flex justify-content-between border rounded p-3 mb-3 bg-light">
                <span>
                  <strong>Payment:</strong>{" "}
                  {previewOrder.paymentInfo?.cashOndelivery ? "Cash on Delivery" : "Prepaid"}
                </span>
                <span>
                  <strong>Total:</strong> ₹{previewOrder.totalPrice}
                </span>
              </div>

              {/* Cancellation Reason */}
              {(previewOrder.orderStatus?.toLowerCase() === "return" ||
                previewOrder.orderStatus?.toLowerCase() === "cancelled") && (
                  <div className="alert alert-danger py-2">
                    <strong>Reason:</strong> {previewOrder.cancellationReason || previewOrder.returnReason || "No reason provided"}
                  </div>
                )}


              {/* Toggle Items */}
              <div className="mb-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowItems((prev) => !prev)}
                >
                  {showItems ? "Hide Items" : "View Items"}
                </Button>

                {showItems && (
                  <ul className="list-group small mt-2">
                    {previewOrder.orderItems?.map((item, idx) => (
                      <li
                        key={idx}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <span className="fw-bold">{item.product?.title || "Unnamed Product"}</span> <br />
                          <small>Qty: {item.quantity}</small>
                        </div>
                        <div className="fw-bold">₹{item.price}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Proof Image */}
              {previewOrder.orderImage && (
                <div className="mt-3 text-center">
                  <h6>Delivery Proof</h6>
                  <img
                    src={`${base_url}${previewOrder.orderImage}`}
                    alt="Order Proof"
                    className="img-fluid rounded shadow-sm"
                    style={{ maxHeight: "250px" }}
                  />
                </div>
              )}
            </div>
          ) : (
            <p>No order details available</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssignOrders;